// Les drills du staff (D-098) : « Ajuster » pour ce lancement seulement (D-139 : la ⚙ est réservée aux réglages), le choix
// d'un drill (exercice, bloc de prépa), le drill de chaque élément d'un plan (le sien, sinon celui de l'exercice), les
// chasubles d'un exercice pour le chrono. La page Basket › Drills et jeux et la feuille « Sur le terrain » (⏱) : D-179,
// drills-jeux.ts.
import './drills.css';
import { currentUserId, teamRoster, type Team } from '../lib/api';
import { displayName } from '../domain/labels';
import { clubDrills, drillsByIds, exerciseLinks, myDrillLibrary, type Drill } from '../lib/drills-api';
import {
  KINDS, KIND_ICON, KIND_LABEL, LEN_MAX, LEN_MIN, drillDurationMs, drillSummary, fmtAbout, fmtSec, nameMatches, readCircuit, readInterval, readReaction,
  type DrillKind,
} from '../domain/drills';
import { BIB_HEX, BIB_LABEL, readGroups } from '../domain/groups';
import type { PlanItem } from '../domain/session';
import { workTeams, type Ctx } from './context';
import { $, $$, html, openSheet } from './dom';
import { drillThumb, modePill } from './drill-view';
import type { Overrides } from './drill-run';
import { openChrono } from './chrono';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Mes équipes encadrées (staff), la première d'abord. */
export const staffTeams = (ctx: Ctx) => workTeams(ctx).filter(t => ctx.staffOf.has(t.id));
/** L'auteur et le bureau modifient un drill ; les autres le dupliquent. */
export const canEditDrill = (ctx: Ctx, d: Drill) => d.created_by === ctx.uid || ctx.adminOf.some(c => c.id === d.club_id);

/** Les chasubles des groupes d'un élément de séance : les équipes du score. */
export function bibsOf(item: PlanItem | undefined): { name: string; color: string }[] {
  return readGroups(item?.groups ?? null).map(g => ({ name: g.bib ? cap(BIB_LABEL[g.bib]) : g.name, color: g.bib ? BIB_HEX[g.bib] : '#9AA1AA' }));
}

/** Le drill de chaque élément d'un plan : le sien (`drill_id`, null = aucun pour cette séance), sinon celui de l'exercice. */
export async function planDrills(items: PlanItem[]): Promise<Map<string, Drill>> {
  const follow = items.filter(i => i.drill_id === undefined && i.exercise_id);
  const links = await exerciseLinks(follow.map(i => i.exercise_id!)).catch(() => []);
  const exDrill = new Map(links.map(l => [l.id, l.drill_id]));
  const want = new Map(items.map(i => [i.id, i.drill_id !== undefined ? i.drill_id : (i.exercise_id ? exDrill.get(i.exercise_id) ?? null : null)]));
  const ids = [...want.values()].filter((x): x is string => !!x);
  const drills = ids.length ? await drillsByIds(ids, true).catch(() => [] as Drill[]) : [];   // même supprimés (D-178) : la séance les garde
  const byId = new Map(drills.map(d => [d.id, d]));
  const out = new Map<string, Drill>();
  for (const [itemId, dId] of want) { const d = dId ? byId.get(dId) : undefined; if (d) out.set(itemId, d); }
  return out;
}

/** Un prénom de l'équipe, pour la vignette d'un drill « prénoms ». */
export const rosterName = async (team: Team | null) => {
  if (!team) return undefined;
  const r = (await teamRoster(team.id).catch(() => [])).find(x => x.kind === 'member');
  return r ? displayName(r) : undefined;
};

/** Le chrono, ouvert sur une équipe, avec les chasubles de l'exercice si on en a. */
export const openTeamChrono = (team: Team | null, bibs?: { name: string; color: string }[]) => void openChrono({ team, bibs });

// -----------------------------------------------------------------------------------------------
// Nouveau drill : le choix du mode
// -----------------------------------------------------------------------------------------------
const KIND_SUB: Record<DrillKind, string> = {
  reaction: 'Flèches, couleurs, signes : au hasard ou en séquence',
  circuit: 'Compteur, compte à rebours, signaux, repos',
  fractionne: 'Effort / récup : Tabata, sprint, HIIT',
};
export function openNewDrill(): void {
  openSheet('Nouveau drill', html`<div class="dr-kinds">${KINDS.map(k => html`
    <button type="button" class="dr-kind" data-kind="${k}"><span aria-hidden="true">${KIND_ICON[k]}</span><span><strong>${KIND_LABEL[k]}</strong><small>${KIND_SUB[k]}</small></span></button>`)}</div>`,
  (sheet, close) => {
    for (const b of $$<HTMLButtonElement>(sheet, '[data-kind]')) b.addEventListener('click', () => { close(); location.hash = `#/basket/drill/${b.dataset.kind}`; });
  });
}

// -----------------------------------------------------------------------------------------------
// Ajuster pour ce lancement seulement
// -----------------------------------------------------------------------------------------------
export function openAdjust(d: Drill, run: (o: Overrides) => void): void {
  const r = readReaction(d.config), itv = readInterval(d.config), c = readCircuit(d.config);
  const o: Overrides = { reaction: {}, fractionne: {}, circuit: {} };
  // En séquence (D-161) : le nombre de tours (ou de minutes), mélanger, le décompte.
  const q = d.kind === 'reaction' && r.mode === 'sequence' && r.sequence ? { ...r.sequence } : null;
  let n = d.kind === 'fractionne' ? itv.cycles : q ? q.repeat.value : r.duration.value;
  const range = (key: string, label: string, ms: number, lo: number, hi: number, step: number) => html`
    <div class="dr-range"><div class="h"><span>${label}</span><b data-v="${key}">${fmtSec(ms)}</b></div><input type="range" data-r="${key}" min="${lo}" max="${hi}" step="${step}" value="${ms}" aria-label="${label}"></div>`;
  const sw = (key: string, label: string, on: boolean) => html`<div class="dr-swrow"><span><strong>${label}</strong></span><button type="button" class="iv-switch" role="switch" data-sw="${key}" aria-checked="${String(on)}" aria-label="${label}"></button></div>`;
  const body = q ? html`
      <div class="dr-durrow"><span>${q.repeat.mode === 'minutes' ? 'Minutes' : 'Tours'}</span><div class="dr-stepper"><button type="button" data-n="-1" aria-label="Moins">−</button><b data-nv>${n}</b><button type="button" data-n="1" aria-label="Plus">+</button></div></div>
      ${sw('shuffle', 'Mélanger', q.shuffle)}${sw('countdown_each', 'Décompte avant chaque tour', q.countdown_each)}${q.countdown_each ? '' : sw('countdown', 'Décompte avant le départ', q.countdown)}`
    : d.kind === 'reaction' ? html`
      ${r.rhythm === 'hasard' ? html`${range('min_ms', 'Signal le plus court', r.min_ms ?? 1000, LEN_MIN, LEN_MAX, 250)}${range('max_ms', 'Signal le plus long', r.max_ms ?? 3000, LEN_MIN, LEN_MAX, 250)}`
        : range('length_ms', 'Durée d’un signal', r.length_ms, LEN_MIN, LEN_MAX, 250)}
      ${range('delay_ms', 'Noir entre deux signaux', r.delay_ms, 0, 5000, 250)}
      <div class="dr-durrow"><span>${r.duration.mode === 'minutes' ? 'Minutes' : 'Signaux'}</span><div class="dr-stepper"><button type="button" data-n="-1" aria-label="Moins">−</button><b data-nv>${n}</b><button type="button" data-n="1" aria-label="Plus">+</button></div></div>
      ${sw('beep', 'Bip à chaque signal', r.beep)}`
    : d.kind === 'fractionne' ? html`
      <div class="dr-durrow"><span>Cycles</span><div class="dr-stepper"><button type="button" data-n="-1" aria-label="Moins">−</button><b data-nv>${n}</b><button type="button" data-n="1" aria-label="Plus">+</button></div></div>
      <div class="row-2"><label class="field"><span>Effort (s)</span><input type="number" data-i="work_s" min="1" max="3600" value="${itv.work_s}"></label>
        <label class="field"><span>Récup (s)</span><input type="number" data-i="rest_s" min="0" max="3600" value="${itv.rest_s}"></label></div>
      ${sw('beeps', 'Bips', itv.beeps)}${sw('voice', 'Voix', itv.voice)}`
    : html`${sw('auto', 'Étape suivante toute seule', c.auto)}${sw('voice', 'Voix', c.voice)}`;
  openSheet(`Ajuster · ${d.title}`, html`
    <p class="dr-sub">Pour ce lancement seulement : rien n’est enregistré.</p>
    <div class="dr-opt">${body}</div>
    <button type="button" class="btn btn-primary" id="dr-adj-go">▶ Lancer</button>`, (sheet, close) => {
    for (const i of $$<HTMLInputElement>(sheet, '[data-r]')) i.addEventListener('input', () => {
      const v = Number(i.value);
      $(sheet, `[data-v="${i.dataset.r}"]`)!.textContent = fmtSec(v);
      (o.reaction as Record<string, number>)[i.dataset.r!] = v;
    });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-n]')) b.addEventListener('click', () => {
      if (q) {
        n = Math.max(1, Math.min(q.repeat.mode === 'minutes' ? 60 : 500, n + Number(b.dataset.n) * (q.repeat.mode === 'minutes' ? 1 : 5)));
        q.repeat = { mode: q.repeat.mode, value: n }; o.reaction!.sequence = q;
        $(sheet, '[data-nv]')!.textContent = String(n);
        return;
      }
      n = Math.max(1, Math.min(d.kind === 'fractionne' ? 99 : r.duration.mode === 'minutes' ? 60 : 500, n + Number(b.dataset.n) * (d.kind === 'reaction' && r.duration.mode === 'signals' ? 5 : 1)));
      $(sheet, '[data-nv]')!.textContent = String(n);
      if (d.kind === 'fractionne') o.fractionne!.cycles = n; else o.reaction!.duration = { mode: r.duration.mode, value: n };
    });
    for (const i of $$<HTMLInputElement>(sheet, '[data-i]')) i.addEventListener('change', () => { (o.fractionne as Record<string, number>)[i.dataset.i!] = Math.max(0, Number(i.value) || 0); });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-sw]')) b.addEventListener('click', () => {
      const on = b.getAttribute('aria-checked') !== 'true';
      b.setAttribute('aria-checked', String(on));
      const k = b.dataset.sw!;
      if (q) { if (k === 'shuffle') q.shuffle = on; else if (k === 'countdown_each') q.countdown_each = on; else q.countdown = on; o.reaction!.sequence = q; }
      else if (d.kind === 'reaction') (o.reaction as Record<string, boolean>)[k] = on;
      else if (d.kind === 'fractionne') (o.fractionne as Record<string, boolean>)[k] = on;
      else (o.circuit as Record<string, boolean>)[k] = on;
    });
    $(sheet, '#dr-adj-go')!.addEventListener('click', () => { close(); run(o); });
  });
}

// -----------------------------------------------------------------------------------------------
// Choisir un drill (l'exercice, le bloc de prépa, l'étape « Signaux » d'un circuit)
// -----------------------------------------------------------------------------------------------
export interface PickOpts {
  clubId: string;
  title?: string;
  kinds?: DrillKind[];
  current?: string | null;
  /** « + Créer un drill » : on revient ici après l'enregistrement (lien d'un exercice). */
  create?: { exerciseId?: string | null; hint?: string };
  hint?: string;
  onPick: (d: Drill) => void;
}
export async function pickDrill(p: PickOpts): Promise<void> {
  const [all, lib, me] = await Promise.all([clubDrills(p.clubId).catch(() => [] as Drill[]), myDrillLibrary().catch(() => new Set<string>()), currentUserId()]);
  const list = all.filter(d => !d.is_model && (!p.kinds || p.kinds.includes(d.kind)));
  const mine = list.filter(d => d.created_by === me || lib.has(d.id));
  const others = list.filter(d => !mine.includes(d));
  openSheet(p.title ?? 'Choisir un drill', html`
    ${list.length > 5 ? html`<input class="search" type="search" placeholder="Chercher un drill" aria-label="Chercher un drill">` : ''}
    ${list.length ? html`<div class="dr-list">${[...mine, ...others].map(d => html`
      <button type="button" class="dr-row" data-d="${d.id}" data-q="${d.title}" style="text-align:left;background:none;border-left:0;border-right:0;border-bottom:0;font:inherit;color:inherit;cursor:pointer;width:100%">
        ${drillThumb(d)}<span class="t"><strong>${d.title}</strong><small>${modePill(d.kind)} · ${drillSummary(d.kind, d.config)}</small></span>
        <span class="chip ${d.id === p.current ? 'chip-ok' : ''}">${d.id === p.current ? 'Choisi' : 'Choisir'}</span></button>`)}</div>`
      : html`<p class="muted">Pas encore de drill${p.kinds ? ` ${p.kinds.map(k => KIND_LABEL[k].toLowerCase()).join(' ou ')}` : ''} au club.</p>`}
    ${p.create ? html`<button type="button" class="btn btn-ghost" id="dr-pick-new">+ Créer un drill</button>` : ''}
    ${p.create?.hint || p.hint ? html`<p class="hint">${p.create?.hint ?? p.hint}</p>` : ''}`, (sheet, close) => {
    const s = $<HTMLInputElement>(sheet, '.search');
    s?.addEventListener('input', () => { for (const b of $$<HTMLElement>(sheet, '[data-q]')) b.hidden = !nameMatches(b.dataset.q!, s.value); });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-d]')) b.addEventListener('click', () => { close(); p.onPick(list.find(d => d.id === b.dataset.d)!); });
    $(sheet, '#dr-pick-new')?.addEventListener('click', () => {
      try { sessionStorage.setItem(PENDING, JSON.stringify({ exercise_id: p.create?.exerciseId ?? null, back: location.hash })); } catch { /* navigation privée */ }
      close(); openNewDrill();
    });
  });
}
/** Un drill créé depuis un exercice : l'éditeur l'associe en enregistrant, puis revient. */
export const PENDING = 'arceau.drill.lier';

// -----------------------------------------------------------------------------------------------
// L'équipe de la page Drills et jeux : celle des prénoms et des essais dans l'éditeur. La page elle-même, « Sur le
// terrain » (⏱), la bibliothèque, Mes créations et le tirage : drills-jeux.ts (D-179).
// -----------------------------------------------------------------------------------------------
let rememberedTeam: string | null = null;
export const rememberTeam = (teamId: string) => { rememberedTeam = teamId; };
export const rememberedTeamId = () => rememberedTeam;

/** « Lecture défensive : flèches · 40 s » (séance en direct). */
export const drillLine = (d: Drill) => `${d.title} · ${fmtAbout(drillDurationMs(d.kind, d.config)).replace(' secondes', ' s')}`;
